import BigNumber from 'bignumber.js'
import { nanoid } from 'nanoid'
import { match, P } from 'ts-pattern'

import { tradeApi } from '@blade/socket-types'
import { Order } from '@blade/std-lib'
import { CurrencyAddress, CurrencyModel } from '@root/entities/currency'
import { currencyRegistry } from '@root/entities/currency/atoms'
import { Network } from '@root/entities/network'
import { PairAddress } from '@root/entities/pair'
import { Provider } from '@root/entities/provider'
import { DelugeId } from '@root/entities/user-short'

import { ActivityId } from './activity-id'

import type { PremiumPurchasesFeedListData } from '@blade/trade-api-client'
import type { PairModel } from '@root/entities/pair'
import type { UserShort } from '@root/entities/user-short'
import type { AvatarsHost, TokenLogosHost } from '@root/shared/models'

export type ActivityBase = {
  address: PairAddress
  network: Network.Network
  occurredAt: Date
  activityId: ActivityId
  currency: CurrencyModel
}

type GrowthWindow = tradeApi.GrowthActivity['window']

export type Growth = ActivityBase & {
  type: 'growth'
  growthPercents: BigNumber
  priceUsd: BigNumber
  marketCapUsd: BigNumber
  window?: GrowthWindow
}
export type HighestAth = ActivityBase & {
  type: 'highest-ath'
  athPriceUsd: BigNumber
  prevAthPriceUsd?: BigNumber
  priceChangePercentSincePrevAth?: BigNumber
}
export type PoolMigrated = ActivityBase & {
  type: 'pool-migrated'
  from: {
    dex: Provider.Provider
    poolAddress: PairAddress
  }
  to: {
    dex: Provider.Provider
    poolAddress: PairAddress
  }
  reason?: string
}

export type BoostPurchased = ActivityBase & {
  type: 'boost-purchased'
  boostersAmount: number
  purchaser: UserShort.UserShort
  pair: PairModel | undefined
}

export type PremiumPurchased = ActivityBase & {
  type: 'premium-purchased'
  boostersAmount: number
  purchaser: UserShort.UserShort
  pair: PairModel | undefined
}

export type Activity =
  | Growth
  | HighestAth
  | PoolMigrated
  | BoostPurchased
  | PremiumPurchased

export type PremiumActivity = PremiumPurchased | BoostPurchased

export const fromDto = (
  dto: tradeApi.PoolActivity | PremiumPurchasesFeedListData[number],
  pair: PairModel | undefined,
  tokenLogosHost: TokenLogosHost,
  avatarsHost: AvatarsHost,
) => {
  const base: ActivityBase = {
    address: PairAddress(dto.address),
    network: Network.fromDto(dto.network),
    occurredAt: new Date(dto.occurredAt),
    activityId: ActivityId(dto.activityId ?? nanoid()),
    currency: currencyRegistry.getOrCreate(
      CurrencyAddress(dto.tokenMeta.address),
      Network.fromDto(dto.tokenMeta.network),
      () => CurrencyModel.fromTokenMeta(dto.tokenMeta, tokenLogosHost),
    ),
  }
  const toUserShort = (
    purchaser: tradeApi.ActivityPurchaser,
  ): UserShort.UserShort => {
    return {
      delugeId: DelugeId(purchaser.delugeId),
      username: purchaser.username ?? '',
      firstName: purchaser.firstName,
      lastName: purchaser.lastName,
      avatarSrc: purchaser.avatar
        ? `${avatarsHost}/${purchaser.avatar}`
        : undefined,
      isOnline: false,
    }
  }

  return match(dto)
    .returnType<Activity>()
    .with({ type: tradeApi.ActivityType.Growth }, (dto): Growth => {
      return {
        ...base,
        type: 'growth',
        growthPercents: BigNumber(dto.growthPercents),
        priceUsd: BigNumber(dto.priceUsd),
        marketCapUsd: BigNumber(dto.marketCapUsd),
        window: dto.window,
      }
    })
    .with({ type: tradeApi.ActivityType.HighestAth }, (dto): HighestAth => {
      return {
        ...base,
        type: 'highest-ath',
        athPriceUsd: BigNumber(dto.athPriceUsd),
        prevAthPriceUsd: dto.prevAthPriceUsd
          ? BigNumber(dto.prevAthPriceUsd)
          : undefined,
        priceChangePercentSincePrevAth: dto.priceChangePercentSincePrevAth
          ? BigNumber(dto.priceChangePercentSincePrevAth)
          : undefined,
      }
    })
    .with({ type: tradeApi.ActivityType.PoolMigrated }, (dto): PoolMigrated => {
      return {
        ...dto,
        ...base,
        from: {
          dex: Provider.byId[dto.from.dex],
          poolAddress: PairAddress(dto.from.poolAddress),
        },
        to: {
          dex: Provider.byId[dto.to.dex],
          poolAddress: PairAddress(dto.to.poolAddress),
        },
        type: 'pool-migrated',
      }
    })
    .with(
      {
        type: P.union(tradeApi.ActivityType.BoostPurchased, 'boost_purchased'),
      },
      (dto): BoostPurchased => ({
        ...base,
        type: 'boost-purchased',
        purchaser: toUserShort(dto.purchaser),
        boostersAmount: dto.boostersAmount!,
        pair,
      }),
    )

    .with(
      {
        type: P.union(
          tradeApi.ActivityType.PremiumPurchased,
          'premium_purchased',
        ),
      },
      (dto): PremiumPurchased => ({
        ...base,
        type: 'premium-purchased',
        purchaser: toUserShort(dto.purchaser),
        boostersAmount: dto.boostersAmount!,
        pair,
      }),
    )
    .exhaustive()
}

export const isPremiumActivity = (
  activity: Activity,
): activity is PremiumActivity => {
  return (
    activity.type === 'premium-purchased' || activity.type === 'boost-purchased'
  )
}

const cmpByTimestampBase = Order.mapInput(
  Order.Date,
  (it: ActivityBase) => it.occurredAt,
)
const cmpByTimestampDescBase = Order.flip(cmpByTimestampBase)

export const cmpByTimestamp = <T extends Activity>(a: T, b: T) => {
  return cmpByTimestampBase(a, b)
}
export const cmpByTimestampDesc: Order.Order<Activity> = (a, b) => {
  return cmpByTimestampDescBase(a, b)
}
